import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendIcon from "@mui/icons-material/Send";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import WhatsAppIcon from "@mui/icons-material/Chat";
import StopIcon from "@mui/icons-material/Stop";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import { useQueryClient } from "@tanstack/react-query";
import { GeneralTable, SingleTableBoxScene } from "@/shared/components/ui";
import { useTableFilter } from "@/shared/hooks";
import {
  ESTADO_CONTACTO_LABEL,
  useCancelarFidelizacion,
  useDescargarPlantillaFidelizacion,
  useEliminarFidelizacion,
  useEnriquecerFidelizacion,
  useEnviarFidelizacionHsm,
  useFidelizacionConfig,
  useFidelizacionContactos,
  useFidelizacionLote,
  useFidelizacionLotes,
  useFidelizacionProceso,
} from "./hooks/useFidelizacionCampana";
import { useColumnsFidelizacion } from "./hooks/useColumnsFidelizacion";
import {
  ConfiguracionEnvioHsm,
  type ValoresEnvioHsm,
} from "./components/ConfiguracionEnvioHsm";
import { EnviarHsmModal } from "./components/EnviarHsmModal";
import { ProgresoFidelizacion } from "./components/ProgresoFidelizacion";
import { SelectorCampanas } from "./components/SelectorCampanas";
import { UploadExcelCard } from "./components/UploadExcelCard";
import type { FidelizacionContacto, FidelizacionLote } from "./fidelizacion-campana.interface";

///* los labels salen del hook: asi el filtro y el chip de la tabla nunca
///* se contradicen. "No contactable" es el aviso de que hay que actualizar el dato
const FILTROS_ESTADO = [
  { value: "", label: "Todos" },
  { value: "PENDIENTE", label: ESTADO_CONTACTO_LABEL.PENDIENTE },
  { value: "CONTACTABLE", label: ESTADO_CONTACTO_LABEL.CONTACTABLE },
  { value: "SIN_TELEFONO", label: ESTADO_CONTACTO_LABEL.SIN_TELEFONO },
  { value: "NO_ENCONTRADO", label: ESTADO_CONTACTO_LABEL.NO_ENCONTRADO },
  { value: "ERROR", label: ESTADO_CONTACTO_LABEL.ERROR },
];

///* Columnas secondary: se pueden activar desde el menu de columnas de la tabla
const COLUMNAS_OCULTAS = {
  fila: false,
  parametros: false,
  respuesta_hsm: false,
  detalle_error: false,
};

///* el detalle se muestra solo si hay filas con error, para no sumar una columna vacia
const COLUMNAS_DETALLE = { detalle_error: true };

///* Encabezado de paso: numero + titulo + descripcion corta + accion a la derecha
const StepCard = ({
  paso,
  titulo,
  descripcion,
  accion,
  children,
}: {
  paso: number;
  titulo: string;
  descripcion?: string;
  accion?: ReactNode;
  children: ReactNode;
}) => (
  <Paper variant="outlined" sx={{ p: 2 }}>
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
      <Avatar sx={{ width: 26, height: 26, fontSize: 13, bgcolor: "primary.main" }}>
        {paso}
      </Avatar>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="subtitle2" fontWeight={600} lineHeight={1.3}>
          {titulo}
        </Typography>
        {descripcion && (
          <Typography variant="caption" color="text.secondary" noWrap>
            {descripcion}
          </Typography>
        )}
      </Box>
      {accion}
    </Stack>
    {children}
  </Paper>
);

///* Metrica del lote (filas, contactables, enviados...)
const StatTile = ({
  label,
  valor,
  color,
}: {
  label: string;
  valor: number;
  color?: string;
}) => (
  <Box
    sx={{
      flex: "1 1 84px",
      minWidth: 84,
      p: 1,
      borderRadius: 1,
      border: "1px solid",
      borderColor: "divider",
      bgcolor: "background.paper",
    }}
  >
    <Typography variant="h6" lineHeight={1.15} color={color ?? "text.primary"}>
      {valor}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export const FidelizacionCampanaPage = () => {
  ///* tabla
  const { pagination, setPagination } = useTableFilter();
  const { pageIndex, pageSize } = pagination;
  const [estado, setEstado] = useState("");
  const [busqueda, setBusqueda] = useState("");

  ///* lote en pantalla
  const [loteId, setLoteId] = useState<number | null>(null);

  ///* paginación y rango de fechas de la lista de campañas del paso 1
  const [paginaLotes, setPaginaLotes] = useState(0);
  const [tamanoLotes, setTamanoLotes] = useState(10);
  const [filtroFechas, setFiltroFechas] = useState<{ fechaDesde?: string; fechaHasta?: string }>({});

  ///* paso 3: plantilla HSM que usará SocialHub para el envío
  const [valoresEnvio, setValoresEnvio] = useState<ValoresEnvioHsm>({
    nombreHsm: "",
  });

  ///* envio
  const [modalEnvio, setModalEnvio] = useState(false);

  const { data: config } = useFidelizacionConfig();
  const { columns } = useColumnsFidelizacion();
  const { data: proceso } = useFidelizacionProceso(loteId);

  ///* con un proceso en marcha los contadores del lote y la tabla se consultan
  ///* solos, para que las barras marcen el avance real y no el de hace un minuto
  const enProceso = proceso?.estado === "CORRIENDO";
  const { data: lotes, isLoading: isLoadingLotes } = useFidelizacionLotes(
    { pagina: paginaLotes, tamano: tamanoLotes, ...filtroFechas },
    enProceso,
  );
  const {
    data: contactos,
    isFetching,
    refetch: refetchContactos,
  } = useFidelizacionContactos(loteId, pageIndex, pageSize, estado, busqueda, enProceso);

  ///* al terminar el proceso los conteos definitivos llegan con un refresco
  const queryClient = useQueryClient();
  const estadoProceso = proceso?.estado;
  useEffect(() => {
    if (!estadoProceso || estadoProceso === "CORRIENDO" || estadoProceso === "SIN_PROCESO") {
      return;
    }
    queryClient.invalidateQueries({ queryKey: ["fidelizacion", "lotes"] });
    queryClient.invalidateQueries({ queryKey: ["fidelizacion", "lote"] });
    queryClient.invalidateQueries({ queryKey: ["fidelizacion", "contactos"] });
  }, [estadoProceso, queryClient]);

  const enriquecer = useEnriquecerFidelizacion();
  const enviar = useEnviarFidelizacionHsm();
  const cancelar = useCancelarFidelizacion();
  const eliminar = useEliminarFidelizacion();
  const plantilla = useDescargarPlantillaFidelizacion();

  ///* el lote en pantalla se pide por id, no de la página visible: si el
  ///* operador cambia de página o de fecha, la campaña elegida sigue en pantalla
  const { data: loteSeleccionado } = useFidelizacionLote(loteId, enProceso);
  const lote: FidelizacionLote | null =
    loteSeleccionado ?? (lotes?.content?.find((item) => item.id === loteId) ?? null);

  ///* la plantilla del lote enviado tiene prioridad sobre la config por defecto
  useEffect(() => {
    if (!lote) return;
    setValoresEnvio((prev) => ({
      nombreHsm:
        lote.nombre_hsm || prev.nombreHsm || config?.nombre_hsm || "",
    }));
  }, [lote, config]);

  ///* una campaña con mensajes ya enviados no se puede eliminar: el lote es el
  ///* registro de a quién se le mandó y con qué plantilla
  const yaEnviada = Boolean(
    lote && (lote.enviados > 0 || lote.estado === "ENVIADO" || lote.estado === "ENVIANDO"),
  );

  const totalContactos = contactos?.total_elements ?? 0;
  const pendientesConsulta = lote?.por_consultar ?? 0;
  const contactables = lote?.contactables ?? 0;

  ///* los que ya se consultaron y el sistema dio una respuesta negativa: sin telefono
  ///* o no registrados. Los que fallaron al consultar van en su propia tarjeta
  ///* "Con error", y los nunca consultados en "Por consultar", para que las cinco
  ///* tarjetas repartan el total sin contar dos veces la misma fila
  const noContactables = (lote?.sin_telefono ?? 0) + (lote?.no_encontrado ?? 0);
  const { descargar: descargarPlantilla } = plantilla;
  const envioCompleto = Boolean(valoresEnvio.nombreHsm.trim());

  ///* elegir una campaña existente muestra su estado; cargar un archivo crea una
  ///* nueva y la deja en pantalla, de ahí en adelante se avanza paso a paso
  const seleccionar = (id: number) => {
    setLoteId(id);
    setPagination({ pageIndex: 0, pageSize: 10 });
  };

  const handleUploaded = (id: number) => {
    ///* se limpian los filtros: la campaña recién cargada es la más reciente y
    ///* con un rango de fechas activo podría no caer en la lista
    setFiltroFechas({});
    setPaginaLotes(0);
    seleccionar(id);
  };

  const handleEnviar = () => {
    if (!loteId || !envioCompleto) return;
    enviar.mutate(
      { loteId, payload: { ...valoresEnvio } },
      {
        onSuccess: () => {
          setModalEnvio(false);
          refetchContactos();
        },
      },
    );
  };

  return (
    <SingleTableBoxScene title="Campañas de Fidelización">
      <Stack spacing={2}>
        {!config?.api_key_configurada && (
          <Alert severity="error">
            El envío no está disponible: falta la API Key de SocialHub. La carga del
            archivo y la consulta de clientes siguen funcionando.
          </Alert>
        )}

        {config?.consulta_clientes?.estado === "RECHAZADO" && (
          <Alert severity="warning">
            El servicio que consulta los datos de los clientes rechazó el token de
            acceso. Las cédulas quedarán marcadas con error hasta que se
            reemplace TOKEN_CONSULTA en el .env:{" "}
            {config.consulta_clientes.ultimo_error ?? ""}
          </Alert>
        )}

        {config?.consulta_clientes?.estado === "SIN_CONFIGURAR" && (
          <Alert severity="info">
            No hay credenciales configuradas para consultar los datos de los clientes.
            Podrás cargar el archivo, pero no se obtendrán nombres ni teléfonos.
          </Alert>
        )}

        {/*///* paso 1: elegir la campaña, ya sea existente o recién cargada */}
        <StepCard
          paso={1}
          titulo="Elegir campaña"
          descripcion="Selecciona una campaña existente para ver su estado, o carga un archivo para empezar una nueva"
          accion={
            <Button
              size="small"
              startIcon={<DownloadIcon />}
              onClick={descargarPlantilla}
              disabled={plantilla.descargando}
            >
              Descargar plantilla
            </Button>
          }
        >
          <Stack spacing={1.5}>
            <SelectorCampanas
              lotes={lotes?.content ?? []}
              seleccionado={loteId}
              onSelect={seleccionar}
              cargando={isLoadingLotes}
              total={lotes?.total_elements ?? 0}
              pagina={paginaLotes}
              tamanoPagina={tamanoLotes}
              onPaginaChange={setPaginaLotes}
              onTamanoChange={(tamano) => {
                setTamanoLotes(tamano);
                setPaginaLotes(0);
              }}
              onFiltrosChange={(filtros) => {
                setFiltroFechas(filtros);
                ///* al filtrar se vuelve a la primera pagina: si no, se puede
                ///* quedar en una pagina que ya no existe
                setPaginaLotes(0);
              }}
            />
            <Divider>
              <Typography variant="caption" color="text.secondary">
                o carga un archivo nuevo
              </Typography>
            </Divider>
            <UploadExcelCard onUploaded={handleUploaded} disabled={enProceso} />
          </Stack>
        </StepCard>

        {/*///* paso 2 */}
        {loteId ? (
          <StepCard
            paso={2}
            titulo="Consultar datos de los clientes"
            descripcion={
              lote ? `Campaña #${lote.id} · ${lote.nombre_archivo}` : undefined
            }
          >
            <Stack spacing={1.5}>
              {lote && (
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <StatTile label="Filas" valor={lote.total_filas} />
                  <StatTile label="Contactables" valor={lote.contactables} color="success.main" />
                  <Tooltip title="Ya se consultaron y el sistema dio una respuesta negativa: no tienen teléfono, o no están registrados. Para contactarlos hay que corregir el dato en el sistema de origen.">
                    <span style={{ display: "inline-flex" }}>
                      <StatTile
                        label="No contactables"
                        valor={noContactables}
                        color={noContactables > 0 ? "warning.main" : undefined}
                      />
                    </span>
                  </Tooltip>
                  <Tooltip title="Filas que todavía nunca se consultaron. Puedes pulsar “Consultar datos” para completarlas.">
                    <span style={{ display: "inline-flex" }}>
                      <StatTile label="Por consultar" valor={lote.por_consultar} />
                    </span>
                  </Tooltip>
                  <StatTile label="Enviados" valor={lote.enviados} color="info.main" />
                  {lote.con_error > 0 && (
                    <Tooltip title="La consulta falló (token vencido, servicio caído) o el envío fue rechazado. Vuelven a entrar en “Consultar datos”.">
                      <span style={{ display: "inline-flex" }}>
                        <StatTile label="Con error" valor={lote.con_error} color="error.main" />
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              )}

              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Tooltip title="Consulta el teléfono de las cédulas sin resultado y de las que quedaron con error">
                  <span>
                    <Button
                      variant="contained"
                      startIcon={<PersonSearchIcon />}
                      onClick={() => enriquecer.mutate(loteId)}
                      disabled={enProceso || enriquecer.isPending || pendientesConsulta === 0}
                    >
                      {pendientesConsulta > 0
                        ? `Consultar datos (${pendientesConsulta})`
                        : "Consultar datos"}
                    </Button>
                  </span>
                </Tooltip>

                <Tooltip
                  title={
                    envioCompleto
                      ? `SocialHub enviará la plantilla a los ${contactables} contactos contactables`
                      : "Elige la plantilla en el paso 3 para habilitar el envío"
                  }
                >
                  <span>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<WhatsAppIcon />}
                      onClick={() => setModalEnvio(true)}
                      disabled={
                        enProceso ||
                        contactables === 0 ||
                        !config?.api_key_configurada ||
                        !envioCompleto
                      }
                    >
                      Enviar campaña ({contactables})
                    </Button>
                  </span>
                </Tooltip>

                {enProceso && (
                  <Button
                    variant="outlined"
                    color="warning"
                    startIcon={<StopIcon />}
                    onClick={() => cancelar.mutate(loteId)}
                  >
                    Detener
                  </Button>
                )}

                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => refetchContactos()}
                  disabled={enProceso}
                >
                  Refrescar
                </Button>

                <Tooltip
                  title={
                    yaEnviada
                      ? "La campaña ya tiene mensajes enviados, así que no se puede eliminar"
                      : enProceso
                        ? "Espera a que termine el proceso en curso"
                        : "Borra el lote y todos sus contactos"
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => {
                        eliminar.mutate(loteId, {
                          onSuccess: () => {
                            setLoteId(null);
                            setPagination({ pageIndex: 0, pageSize: 10 });
                          },
                        });
                      }}
                      disabled={enProceso || yaEnviada || eliminar.isPending}
                    >
                      Eliminar campaña
                    </Button>
                  </span>
                </Tooltip>
              </Stack>

            </Stack>
          </StepCard>
        ) : (
          <Alert severity="info">Carga un archivo para ver la vista previa.</Alert>
        )}

        {/*///* paso 3 */}
        {loteId && (
          <StepCard
            paso={3}
            titulo="Configurar el envío"
            descripcion="Plantilla de SocialHub y los datos que viajan con el mensaje"
          >
            <ConfiguracionEnvioHsm
              config={config}
              lote={lote}
              valores={valoresEnvio}
            />
          </StepCard>
        )}

        {/*///* avance de la consulta y del envio */}
        {loteId && <ProgresoFidelizacion lote={lote} proceso={proceso ?? null} />}

        {/*///* tabla */}
        {loteId && (
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap">
              <TextField
                select
                size="small"
                label="Estado"
                value={estado}
                onChange={(e) => {
                  setEstado(e.target.value);
                  setPagination({ pageIndex: 0, pageSize: 10 });
                }}
                sx={{ minWidth: 170 }}
              >
                {FILTROS_ESTADO.map((filtro) => (
                  <MenuItem key={filtro.value} value={filtro.value}>
                    {filtro.label}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                size="small"
                placeholder="Buscar por cédula, idContrato o nombre"
                value={busqueda}
                inputProps={{ "aria-label": "Buscar contactos" }}
                onChange={(e) => setBusqueda(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    setPagination({ pageIndex: 0, pageSize: 10 });
                    refetchContactos();
                  }
                }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon fontSize="small" sx={{ mr: 1, color: "text.secondary" }} />
                  ),
                }}
                sx={{ minWidth: 260, flex: 1, maxWidth: 380 }}
              />
            </Stack>

            <GeneralTable<FidelizacionContacto>
              columns={columns}
              data={contactos?.content ?? []}
              isLoading={isFetching}
              enableGlobalFilter={false}
              enableColumnFilters={false}
              enableSorting={false}
              enableColumnActions
              canEdit={false}
              enableEditing={false}
              positionActionsColumn="last"
              pagination={pagination}
              onPaging={setPagination}
              rowCount={totalContactos}
              getRowId={(row) => String(row.id)}
              initialColumnVisibility={
                (lote?.con_error ?? 0) > 0
                  ? { ...COLUMNAS_OCULTAS, ...COLUMNAS_DETALLE }
                  : COLUMNAS_OCULTAS
              }
              muiTableContainerProps={{ sx: { overflowX: "auto" } }}
            />
          </Stack>
        )}
      </Stack>

      <EnviarHsmModal
        open={modalEnvio}
        onClose={() => setModalEnvio(false)}
        onConfirm={handleEnviar}
        loading={enviar.isPending}
        lote={lote}
        nombreHsm={valoresEnvio.nombreHsm}
        dryRun={config?.dry_run}
      />
    </SingleTableBoxScene>
  );
};

export default FidelizacionCampanaPage;
